import React, { useEffect } from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Grid,
  Alert,
  FormControl,
  FormLabel,
  TextField,
  Select,
  MenuItem
} from '@mui/material';
import {
  Bed as BedIcon,
  Hotel as HotelIcon,
  Badge as BadgeIcon,
  CalendarToday as CalendarTodayIcon,
  AttachMoney as AttachMoneyIcon
} from '@mui/icons-material';
import moment from 'moment';

import { STAY_TYPE_NIGHT, STAY_TYPE_PARTIAL } from './constants';
import { GRADIENT_PRIMARY, SHADOW_CARD, COLOR_FIELD_BG_READONLY, COLOR_BORDER_NEUTRAL } from './theme';

const RoomDetailsStep = ({
  values,
  setFieldValue,
  errors,
  touched,
  selectedRoomData,
  currency,
  amount,
  calculateAmount,
  stayTypes
}) => {
  const selectedStayType = stayTypes?.find((type) => type._id === values.stayType);
  const isNightly = selectedStayType
    ? selectedStayType.isNightly
    : values.stayType === STAY_TYPE_NIGHT || !values.stayType;

  // Para estancias por noche, calculamos el monto cuando falten datos
  // de salida. No reescribe nada si no es necesario.
  //
  // values.roomNo y values.stayType están en las deps a propósito: al entrar
  // a la página, Formik re-inicializa el formulario en varios pasos (habitación
  // y stay type llegan de fetches distintos) y cada reinit puede borrar el
  // checkOutDate calculado. Con estas deps, el último reinit vuelve a disparar
  // el cálculo; sin ellas, la fecha quedaba vacía y "Siguiente" bloqueado hasta
  // que el usuario cambiara la estancia manualmente.
  useEffect(() => {
    if (isNightly && !values.checkOutDate && values.checkInDate && selectedRoomData) {
      calculateAmount(values, setFieldValue);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isNightly, values.checkInDate, values.checkOutDate, values.roomNo, values.stayType, selectedRoomData]);

  const handleCheckInChange = (newCheckIn) => {
    let next = newCheckIn;

    if (next && isNightly) {
      const selectedMoment = moment(next, 'YYYY-MM-DDTHH:mm');
      const todayMoment = moment().startOf('day');
      const selectedDateMoment = selectedMoment.clone().startOf('day');

      // Si la fecha es futura, aplicamos la hora de check-in del hotel.
      if (selectedDateMoment.isAfter(todayMoment)) {
        const hotelData = JSON.parse(localStorage.getItem('hotelData') || '{}');
        let baseCheckinTime = moment('14:00', 'HH:mm');
        if (hotelData.checkInTime) {
          const timeObj = moment(hotelData.checkInTime, ['h:mm A', 'HH:mm']);
          if (timeObj.isValid()) baseCheckinTime = timeObj;
        }
        selectedMoment.set({
          hour: baseCheckinTime.get('hour'),
          minute: baseCheckinTime.get('minute'),
          second: 0
        });
        next = selectedMoment.format('YYYY-MM-DDTHH:mm');
      }
    }

    setFieldValue('checkInDate', next);
    calculateAmount({ ...values, checkInDate: next }, setFieldValue);
  };

  return (
    <Card sx={{ mb: 3, boxShadow: SHADOW_CARD }}>
      <Box sx={{ background: GRADIENT_PRIMARY, p: 2, color: 'white' }}>
        <Box display="flex" alignItems="center" gap={2}>
          <BedIcon />
          <Typography variant="h6" fontWeight="bold">
            Detalles de la Habitación
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ p: 3 }}>
        <Grid container spacing={3}>
          {selectedRoomData && (
            <Grid item xs={12}>
              <Alert icon={<HotelIcon />} severity="info" sx={{ mb: 2 }}>
                <Typography variant="body2">
                  <strong>Habitación seleccionada:</strong> {selectedRoomData.roomType} - #{selectedRoomData.roomNo}
                </Typography>
              </Alert>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                <HotelIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Tipo de Habitación
              </FormLabel>
              <TextField
                name="roomType"
                size="small"
                fullWidth
                placeholder="Ej: Suite, Deluxe, Standard"
                value={selectedRoomData?.roomType || ''}
                onChange={(e) => setFieldValue('roomType', e.target.value)}
                error={errors.roomType && Boolean(errors.roomType)}
                helperText={touched.roomType && errors.roomType}
                InputProps={{
                  readOnly: !!selectedRoomData?.roomType,
                  sx: {
                    bgcolor: selectedRoomData?.roomType ? COLOR_FIELD_BG_READONLY : 'white',
                    '& .MuiOutlinedInput-notchedOutline': { borderColor: COLOR_BORDER_NEUTRAL }
                  }
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                <BadgeIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Número de Habitación
              </FormLabel>
              <TextField
                name="roomNo"
                size="small"
                fullWidth
                placeholder="Ej: 101, 202"
                value={selectedRoomData?.roomNo || ''}
                onChange={(e) => {
                  setFieldValue('roomNo', e.target.value);
                  calculateAmount({ ...values, roomNo: e.target.value }, setFieldValue);
                }}
                error={errors.roomNo && Boolean(errors.roomNo)}
                helperText={touched.roomNo && errors.roomNo}
                InputProps={{
                  readOnly: !!selectedRoomData?.roomNo,
                  sx: { bgcolor: selectedRoomData?.roomNo ? COLOR_FIELD_BG_READONLY : 'white' }
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Tipo de Estancia
              </FormLabel>
              <Select
                name="stayType"
                value={values.stayType}
                onChange={(e) => {
                  const next = e.target.value;
                  setFieldValue('stayType', next);
                  calculateAmount({ ...values, stayType: next }, setFieldValue);
                }}
                size="small"
                sx={{ bgcolor: 'white' }}
              >
                {stayTypes?.map((type) => (
                  <MenuItem key={type._id} value={type._id}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <CalendarTodayIcon fontSize="small" />
                      {type.label} ({type.isNightly ? 'Nightly' : `${type.hours}h`})
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {isNightly && (
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                  <CalendarTodayIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                  Número de Noches
                </FormLabel>
                <TextField
                  name="numberOfNights"
                  type="number"
                  size="small"
                  fullWidth
                  InputProps={{ inputProps: { min: 1 } }}
                  value={values.numberOfNights || 1}
                  onChange={(e) => {
                    const nights = Math.max(1, parseInt(e.target.value, 10) || 1);
                    setFieldValue('numberOfNights', nights);
                    calculateAmount({ ...values, numberOfNights: nights }, setFieldValue);
                  }}
                  sx={{ bgcolor: 'white' }}
                />
              </FormControl>
            </Grid>
          )}

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>Fecha de Entrada</FormLabel>
              <TextField
                name="checkInDate"
                size="small"
                type="datetime-local"
                fullWidth
                value={values.checkInDate}
                onChange={(e) => handleCheckInChange(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={errors.checkInDate && Boolean(errors.checkInDate)}
                helperText={touched.checkInDate && errors.checkInDate}
                sx={{ bgcolor: 'white' }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>Fecha de Salida</FormLabel>
              <TextField
                name="checkOutDate"
                size="small"
                type="datetime-local"
                fullWidth
                value={values.checkOutDate}
                onChange={(e) => {
                  const next = e.target.value;
                  setFieldValue('checkOutDate', next);
                  calculateAmount({ ...values, checkOutDate: next }, setFieldValue);
                }}
                disabled={isNightly || values.stayType === STAY_TYPE_PARTIAL}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: values.checkInDate }}
                error={errors.checkOutDate && Boolean(errors.checkOutDate)}
                helperText={touched.checkOutDate && errors.checkOutDate}
                sx={{
                  bgcolor: isNightly || values.stayType === STAY_TYPE_PARTIAL ? COLOR_FIELD_BG_READONLY : 'white'
                }}
              />
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <FormLabel sx={{ mb: 1, fontWeight: 600, color: 'text.primary' }}>
                <AttachMoneyIcon sx={{ fontSize: 18, mr: 0.5, verticalAlign: 'middle' }} />
                Monto Total
              </FormLabel>
              <TextField
                size="small"
                fullWidth
                value={`${currency} ${amount}`}
                disabled
                InputProps={{
                  sx: {
                    bgcolor: COLOR_FIELD_BG_READONLY,
                    fontWeight: 'bold',
                    fontSize: '1.1rem',
                    color: 'primary.main'
                  }
                }}
              />
            </FormControl>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default RoomDetailsStep;
